import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  Search,
  MoreHorizontal,
  ClipboardList,
  Loader2,
  Trash2,
  Mail,
  Phone,
  Building2,
  Globe,
  Calendar,
  User,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type InquiryStatus = 'NEW' | 'IN_PROGRESS' | 'QUOTED' | 'WON' | 'LOST' | 'CANCELLED';

interface TradingInquiry {
  id: string;
  inquiryNumber: string;
  companyName?: string | null;
  contactName: string;
  email: string;
  phone?: string | null;
  country?: string | null;
  message?: string | null;
  status: InquiryStatus;
  internalNotes?: string | null;
  assignedToId?: string | null;
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  items: Array<{
    id: string;
    quantity: number;
    notes?: string | null;
    fullCode: string;
    productId: string;
    inquiryId: string;
    product: {
      id: string;
      name: string;
      code: string;
      model: {
        code: string;
        brand: {
          code: string;
        };
      };
    };
  }>;
  createdAt: string | Date;
  updatedAt: string | Date;
}

const statusColors: Record<InquiryStatus, string> = {
  NEW: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  QUOTED: 'bg-purple-100 text-purple-700',
  WON: 'bg-green-100 text-green-700',
  LOST: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-700',
};

export function TradingInquiriesPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  // Permission checks (canCreate unused - inquiries come from website)
  const _canCreate = usePermission('INQUIRIES', 'CREATE');
  const canEdit = usePermission('INQUIRIES', 'EDIT');
  const canDelete = usePermission('INQUIRIES', 'DELETE');
  void _canCreate; // Available for future use
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | undefined>();
  const [page, setPage] = useState(1);
  const [selectedInquiry, setSelectedInquiry] = useState<TradingInquiry | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [inquiryToDelete, setInquiryToDelete] = useState<string | null>(null);
  const limit = 10;

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.tradingInquiry.list.useQuery({
    page,
    limit,
    search: debouncedSearch || undefined,
    status: statusFilter,
  });

  const { data: stats } = trpc.tradingInquiry.stats.useQuery();

  const utils = trpc.useUtils();

  const updateStatusMutation = trpc.tradingInquiry.updateStatus.useMutation({
    onSuccess: () => {
      utils.tradingInquiry.list.invalidate();
      utils.tradingInquiry.stats.invalidate();
      showSuccessToast(t('common.updated'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const deleteMutation = trpc.tradingInquiry.delete.useMutation({
    onSuccess: () => {
      utils.tradingInquiry.list.invalidate();
      utils.tradingInquiry.stats.invalidate();
      showSuccessToast(t('common.deleted'));
      setDeleteConfirmOpen(false);
      setInquiryToDelete(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleStatusChange = (id: string, status: InquiryStatus) => {
    updateStatusMutation.mutate({ id, status });
  };

  const handleDeleteInquiry = (id: string) => {
    setInquiryToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (inquiryToDelete) {
      deleteMutation.mutate(inquiryToDelete);
    }
  };

  const inquiries = data?.inquiries ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 10, total: 0, totalPages: 0 };

  const getStatusKey = (status: InquiryStatus): string => {
    const map: Record<InquiryStatus, string> = {
      NEW: 'new',
      IN_PROGRESS: 'inProgress',
      QUOTED: 'quoted',
      WON: 'won',
      LOST: 'lost',
      CANCELLED: 'cancelled',
    };
    return map[status];
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('inquiries.title')}</h1>
          <p className="text-muted-foreground">
            {t('inquiries.subtitle')}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <div className="border border-border bg-card p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">{t('inquiries.stats.total')}</div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.new}</div>
            <div className="text-sm text-muted-foreground">{t('inquiries.stats.newInquiries')}</div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.inProgress}</div>
            <div className="text-sm text-muted-foreground">{t('inquiries.stats.inProgress')}</div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.quoted}</div>
            <div className="text-sm text-muted-foreground">{t('inquiries.stats.quoted')}</div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="text-2xl font-bold text-green-600">{stats.won}</div>
            <div className="text-sm text-muted-foreground">{t('inquiries.stats.wonDeals')}</div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('inquiries.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Inquiry Detail Dialog */}
      <Dialog open={!!selectedInquiry} onOpenChange={() => setSelectedInquiry(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('inquiries.inquiryNumber')}: {selectedInquiry?.inquiryNumber}
            </DialogTitle>
            <DialogDescription>
              {t('inquiries.contactInfo')}
            </DialogDescription>
          </DialogHeader>

          {selectedInquiry && (
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{selectedInquiry.contactName}</span>
                  </div>
                  {selectedInquiry.companyName && (
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedInquiry.companyName}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${selectedInquiry.email}`} className="text-primary hover:underline">
                      {selectedInquiry.email}
                    </a>
                  </div>
                  {selectedInquiry.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedInquiry.phone}</span>
                    </div>
                  )}
                  {selectedInquiry.country && (
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedInquiry.country}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{new Date(selectedInquiry.createdAt).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className={`px-2 py-0.5 text-xs font-medium ${statusColors[selectedInquiry.status]}`}>
                      {t(`inquiries.${getStatusKey(selectedInquiry.status)}`)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Message */}
              {selectedInquiry.message && (
                <div className="space-y-2">
                  <h4 className="font-medium">{t('inquiries.message')}</h4>
                  <div className="bg-muted p-3 text-sm">
                    {selectedInquiry.message}
                  </div>
                </div>
              )}

              {/* Products */}
              <div className="space-y-2">
                <h4 className="font-medium">{t('inquiries.products')}</h4>
                <div className="border border-border divide-y">
                  {selectedInquiry.items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{item.product.name}</div>
                          <div className="text-sm text-muted-foreground">{item.fullCode}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{t('inquiries.quantity')}: {item.quantity}</div>
                        {item.notes && (
                          <div className="text-sm text-muted-foreground">{item.notes}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Change */}
              {canEdit && (
                <div className="space-y-2">
                  <h4 className="font-medium">{t('inquiries.status')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {(['NEW', 'IN_PROGRESS', 'QUOTED', 'WON', 'LOST', 'CANCELLED'] as InquiryStatus[]).map((status) => (
                      <Button
                        key={status}
                        variant={selectedInquiry.status === status ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          handleStatusChange(selectedInquiry.id, status);
                          setSelectedInquiry({ ...selectedInquiry, status });
                        }}
                      >
                        {t(`inquiries.${getStatusKey(status)}`)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('inquiries.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-8 w-full border border-border bg-background pl-10 pr-4 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
          />
        </div>
        <select
          value={statusFilter ?? ''}
          onChange={(e) => {
            setStatusFilter((e.target.value as InquiryStatus) || undefined);
            setPage(1);
          }}
          className="h-8 border border-border bg-background px-3 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
        >
          <option value="">{t('inquiries.allStatuses')}</option>
          <option value="NEW">{t('inquiries.new')}</option>
          <option value="IN_PROGRESS">{t('inquiries.inProgress')}</option>
          <option value="QUOTED">{t('inquiries.quoted')}</option>
          <option value="WON">{t('inquiries.won')}</option>
          <option value="LOST">{t('inquiries.lost')}</option>
          <option value="CANCELLED">{t('inquiries.cancelled')}</option>
        </select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && inquiries.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ClipboardList className="h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">{t('inquiries.noInquiries')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('inquiries.noInquiriesDesc')}
          </p>
        </div>
      )}

      {/* Inquiries Table */}
      {!isLoading && !error && inquiries.length > 0 && (
        <>
          <div className="border border-border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inquiries.inquiryNumber')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inquiries.contactInfo')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inquiries.products')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inquiries.status')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inquiries.created')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {inquiries.map((inquiry) => (
                  <tr
                    key={inquiry.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => setSelectedInquiry(inquiry)}
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono font-medium">{inquiry.inquiryNumber}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{inquiry.contactName}</div>
                      {inquiry.companyName && (
                        <div className="text-sm text-muted-foreground">{inquiry.companyName}</div>
                      )}
                      <div className="text-sm text-muted-foreground">{inquiry.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        {inquiry.items.length} {inquiry.items.length === 1 ? 'product' : 'products'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {inquiry.items.slice(0, 2).map((item) => item.product.name).join(', ')}
                        {inquiry.items.length > 2 && ` +${inquiry.items.length - 2} more`}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs font-medium ${statusColors[inquiry.status]}`}>
                        {t(`inquiries.${getStatusKey(inquiry.status)}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(inquiry.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            setSelectedInquiry(inquiry);
                          }}>
                            {t('common.view')}
                          </DropdownMenuItem>
                          {canDelete && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteInquiry(inquiry.id);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {tCommon('delete')}
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page <= 1}
              >
                {tCommon('previous')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {pagination.page} / {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={pagination.page >= pagination.totalPages}
              >
                {tCommon('next')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
